import React from "react";
import { Redirect, withRouter, useParams, useHistory } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useQuery } from "@apollo/client";
import { STREETS_COMMON_QUERY, DISTRICT_ONE_QUERY } from "Services/queries";
import { generateUrl } from "Services/withDefaultParams";
import useDefaultParams from "Services/useDefaultParams";
import { generateStreetPart, generateValidChankUrl } from "Helpers/UrlManager";
import Routes from "Configs/routes";
import { BigBlockStub } from "Layouts/Stubs";
import SeoAddressLayout from "Layouts/SeoAddressLayout";
import SeoBlockHOC from "HOC/SeoBlockHOC";
import AddressSearchContainer from "Containers/AddressSearchContainer";
import List from "ComponentsUI/List";
import { Redirect404 } from "ComponentsUI/Redirects";
import ProvidersContainer from "./ProvidersContainer";
import { isMainSiteCode } from "Utils/siteConfig";

const Streets = (props) => {
	const {
		ApplicationModel: {
			siteCode,
			siteConfig,
			text,
			windowSizeModel: { windowSizes },
		},
		match: { params },
		route,
	} = props;

	const routerParams = useParams();
	const historyParams = useHistory();
	const defaultParams = useDefaultParams(routerParams);
	const districtOneQuery = useQuery(DISTRICT_ONE_QUERY, {
		variables: {
			id: +routerParams.districtId,
		},
	});

	const canonical = () => {
		const location = generateUrl(Routes.AddressStreets.route, {
			regionUrl: params.regionUrl,
			districtName: params.districtName,
			districtId: params.districtId,
		});

		return (
			<Helmet>
				<link
					rel="canonical"
					href={`${siteConfig.ssl === true ? `https` : "http"}://${
						siteConfig.domain_name_full
					}${location}`}
				/>
			</Helmet>
		);
	};

	const streetsQuery = useQuery(STREETS_COMMON_QUERY, {
		variables: {
			filter: `district_id=${routerParams.districtId}`,
			regionUrl: defaultParams.regionUrl,
		},
	});

	let isStub = streetsQuery.loading || districtOneQuery.loading;

	let isSettlement = false;

	let middleComponent = <BigBlockStub />;
	const districtsOne = districtOneQuery?.data?.districtsOne;
	if (!isStub) {
		const streets = streetsQuery.data.streets.data;

		if (!districtsOne) {
			let location = {
				pathname: generateUrl(Routes.AddressDistricts.route, routerParams),
				state: {
					isRedirect: true,
				},
			};
			return <Redirect to={location} />;
		}
		if (districtsOne.region.id !== props.ApplicationModel.currentRegion?.id) {
			return (
				<Redirect404
					message404={"districtsOne.region.id !== props.ApplicationModel.currentRegion.id"}
				/>
			);
		}

		if (generateValidChankUrl(districtsOne.name.toLowerCase()) !== routerParams.districtName) {
			let link = decodeURIComponent(
				generateUrl(Routes.AddressStreets.route, {
					regionUrl: defaultParams.regionUrl,
					districtName: generateValidChankUrl(districtsOne.name.toLowerCase()),
					districtId: districtsOne.id,
				})
			);

			let location = {
				pathname: link,
				search: historyParams.search,
				state: {
					isRedirect: true,
				},
			};
			return <Redirect to={location} />;
		}

		isSettlement = !!districtsOne.service;
		let cloneStreets = JSON.parse(JSON.stringify(streets));
		cloneStreets = cloneStreets.map((s) => {
			return {
				...s,
				name: s.name.charAt(0).toUpperCase() + s.name.slice(1),
				full: s.full.charAt(0).toUpperCase() + s.full.slice(1),
			};
		});
		let sortStreets = cloneStreets.sort(function (a, b) {
			if (a.full >= b.full) return 1;
			if (a.full < b.full) return -1;
		});

		let sortStreets_ = [];

		sortStreets.map((street) => {
			sortStreets_.push({
				id: street.id,
				name: street.full,
				full: street.full,
				region_id: street.region_id,
				district: districtsOne,
				link: decodeURIComponent(
					generateUrl(Routes.AddressStreet.route, {
						regionUrl: defaultParams.regionUrl,
						districtName: generateValidChankUrl(street.district.name),
						districtId: street.district.id,
						streetName: generateStreetPart(street),
						streetId: street.id,
					})
				),
			});
		});
		middleComponent = (
			<List
				streetsClass={true}
				text={text}
				defaultItems={sortStreets_}
				windowSizes={windowSizes}
				showAlphabet={true}
				type={"streets"}
				listHeader={
					isSettlement
						? `${text.SEO_ADDRESS_SETTLEMENTS_TITLE}${text.INTO}${districtsOne.name}`
						: `${text.SEO_ADDRESS_STREETS_TITLE}${districtsOne.name}`
				}
			/>
		);
	}
	return (
		<React.Fragment>
			{canonical()}
			{isMainSiteCode(siteCode) && <div></div>}
			<SeoBlockHOC route={route} placeText="AddressSearch">
				<AddressSearchContainer />
			</SeoBlockHOC>
			<SeoBlockHOC route={route} placeText="ProvidersContainer">
				<ProvidersContainer ApplicationModel={props.ApplicationModel} districtsOne={districtsOne} />
			</SeoBlockHOC>
			<SeoAddressLayout topComponent={null} middleComponent={middleComponent} />
		</React.Fragment>
	);
};

export default withRouter(Streets);
